/**
 * Owner: Masa Bayu
 * File: spsu-universal-pipeline-bridge.ts
 * Date:Time: 2026-10-05 14:40
 * Description: Fasa 20+21 · SPSU → Universal · pass real fontSize (not bbox.h).
 */

import { resolveFont } from '@qx/spsu-line-break-optimizer';
import { adaptToClassifier } from '@qx/qx-universal-engine';
import {
  processRawPdfData,
  type RawPdfItem,
} from '@qx/qx-universal-engine';
import { classifyPage } from '@qx/spsu-structural-classifier';
import type { UniversalAtom } from '@qx/qx-universal-engine';
import type {
  AssetLibrary,
  ImageElement,
  SPSUElement,
  TextElement,
} from '../extract/spsu-types';

/** BaseFont for pipeline · FontDescriptor sourceFontName when asset match exists. */
function baseFontForText(el: TextElement, assets: AssetLibrary): string | undefined {
  const fonts = Object.values(assets).filter(
    (a) => a.type === 'font' && a.font != null,
  );

  const direct = fonts.find((a) => a.font!.familyName === el.style.fontFamily);
  if (direct?.font) return direct.font.sourceFontName;

  const normalized = resolveFont(el.style.fontFamily).fontFamily;
  const byNorm = fonts.find((a) => a.font!.familyName === normalized);
  if (byNorm?.font) return byNorm.font.sourceFontName;

  return undefined;
}

export function spsuElementsToRawPdfItems(
  elements: readonly SPSUElement[],
  assets: AssetLibrary,
): RawPdfItem[] {
  const raw: RawPdfItem[] = [];

  for (const el of elements) {
    if (el.type === 'text') {
      const baseFont = baseFontForText(el, assets);
      const item: RawPdfItem = {
        id: el.id,
        type: 'text',
        str: el.content,
        fontName: el.style.fontFamily,
        fontSize: el.style.fontSize,
        bbox: {
          x: el.bbox.x,
          y: el.bbox.y,
          width: el.bbox.width,
          height: el.bbox.height,
        },
        color: el.style.color,
      };
      if (baseFont) {
        (item as { fontObj?: { name: string } }).fontObj = { name: baseFont };
      }
      raw.push(item);
      continue;
    }
    if (el.type === 'image') {
      raw.push({
        id: el.id,
        type: 'image',
        assetId: el.assetId,
        bbox: {
          x: el.bbox.x,
          y: el.bbox.y,
          width: el.bbox.width,
          height: el.bbox.height,
        },
      });
    }
  }

  return raw;
}

export function enrichTextFromUniversal(
  el: TextElement,
  atom: UniversalAtom | undefined,
): TextElement {
  if (!atom || atom.type !== 'text' || !atom.font) return el;
  return {
    ...el,
    style: {
      ...el.style,
      fontFamily: atom.font.family,
      fontSize: atom.font.size > 0 ? atom.font.size : el.style.fontSize,
      fontWeight: atom.font.weight,
      fontStyle: atom.font.style,
      ...(atom.color ? { color: atom.color } : {}),
    },
  };
}

export type UniversalPipelineResult = {
  readonly universalById: ReadonlyMap<string, UniversalAtom>;
  readonly structured: ReturnType<typeof classifyPage>;
};

export function runUniversalPipeline(
  elements: readonly SPSUElement[],
  assets: AssetLibrary,
  pageHeight: number,
): UniversalPipelineResult {
  const raw = spsuElementsToRawPdfItems(elements, assets);
  const universal = processRawPdfData(raw);
  const universalById = new Map(universal.map((a) => [a.id, a] as const));
  const flat = universal.map(adaptToClassifier);
  const structured = classifyPage(flat, pageHeight);
  return { universalById, structured };
}

export function asEnrichedText(
  el: SPSUElement | undefined,
  universalById: ReadonlyMap<string, UniversalAtom>,
): TextElement | null {
  if (!el || el.type !== 'text') return null;
  return enrichTextFromUniversal(el, universalById.get(el.id));
}

export function asImageElement(el: SPSUElement | undefined): ImageElement | null {
  return el && el.type === 'image' ? el : null;
}
